import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { Scholar, ScholarshipProgram, UserRole } from '../types';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import { yearLevelCategoryOf, YEAR_LEVEL_CATEGORY_OPTIONS } from '../constants/yearLevelCategory';

const STATUS_BADGE: Record<string, string> = {
  active: 'badge-success',
  inactive: 'badge-secondary',
  graduated: 'badge-primary',
  terminated: 'badge-error'
};

const STATUS_OPTIONS = ['active', 'inactive', 'graduated', 'terminated'];

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

interface ProgramGroup {
  scholarshipName: string;
  scholars: Scholar[];
}

function programKey(scholar: Scholar): string {
  return scholar.scholarshipName ?? `Scholarship #${scholar.scholarshipId}`;
}

// Groups by scholarship program, then orders each group by submissionDate
// ascending (earliest submitted first) so the row number reflects the
// order applicants actually submitted in — scholars with no known
// submission date (e.g. manually approved without ever being submitted)
// sort last within their group.
function groupByProgram(scholars: Scholar[]): ProgramGroup[] {
  const groups = new Map<string, Scholar[]>();
  for (const scholar of scholars) {
    const key = programKey(scholar);
    const group = groups.get(key);
    if (group) group.push(scholar);
    else groups.set(key, [scholar]);
  }

  return Array.from(groups.entries())
    .map(([scholarshipName, groupScholars]) => ({
      scholarshipName,
      scholars: [...groupScholars].sort((a, b) => {
        if (!a.submissionDate && !b.submissionDate) return 0;
        if (!a.submissionDate) return 1;
        if (!b.submissionDate) return -1;
        return new Date(a.submissionDate).getTime() - new Date(b.submissionDate).getTime();
      })
    }))
    .sort((a, b) => a.scholarshipName.localeCompare(b.scholarshipName));
}

const ScholarManagementPage = () => {
  const { user } = useAuth();
  const isViewer = user?.role === UserRole.VIEWER;
  const [scholars, setScholars] = useState<Scholar[]>([]);
  const [programs, setPrograms] = useState<ScholarshipProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Stores a scholarshipId (as a string, to match <select> values) so
  // every existing AND newly created program is selectable — including
  // ones with zero scholars yet — not just programs already represented
  // among current scholars.
  const [programFilter, setProgramFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [barangaySearch, setBarangaySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deletingScholar, setDeletingScholar] = useState<Scholar | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    apiService
      .getScholars({ pageSize: 100 })
      .then((result) => setScholars(result.data))
      .catch((err) => setError(err.message || 'Failed to load scholars'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    apiService
      .getScholarships(1, 100)
      .then((result) => setPrograms(result.data))
      .catch(() => {
        // Non-fatal — the Program filter just won't have options if this fails.
      });
  }, []);

  const handleConfirmDelete = async () => {
    if (!deletingScholar) return;
    setDeleting(true);
    setError('');
    setNotice('');
    try {
      const result = await apiService.deleteScholar(deletingScholar.id);
      setDeletingScholar(null);
      if (result.status === 'pending') setNotice(result.message);
      load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete scholar');
    } finally {
      setDeleting(false);
    }
  };


  const filtered = scholars.filter((scholar) => {
    if (programFilter && scholar.scholarshipId !== Number(programFilter)) return false;
    if (statusFilter && scholar.status !== statusFilter) return false;
    if (categoryFilter && yearLevelCategoryOf(scholar.yearLevel) !== categoryFilter) return false;
    if (barangaySearch) {
      const q = barangaySearch.toLowerCase();
      const matchesBarangay = scholar.studentBarangay?.toLowerCase().includes(q);
      // Some applicants entered their barangay as free text under the
      // legacy address field instead of the structured barangay field —
      // fall back to matching that too, so they're still findable.
      const matchesAddress = scholar.studentAddress?.toLowerCase().includes(q);
      if (!matchesBarangay && !matchesAddress) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = scholar.studentName?.toLowerCase().includes(q);
      const matchesEmail = scholar.studentEmail?.toLowerCase().includes(q);
      if (!matchesName && !matchesEmail) return false;
    }
    return true;
  });

  const displayGroups = groupByProgram(filtered);

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Scholars</div>
        {/* Bulk "Delete All" has been disabled system-wide — the server
            refuses DELETE /scholars regardless, and the button is removed
            so it is not offered. */}
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Scholar Management</h1>
          <p>
            Track grades, renewals, allowances, and compliance for active scholars.{' '}
            {!loading && <strong>Total: {scholars.length.toLocaleString()} scholar{scholars.length === 1 ? '' : 's'}</strong>}
          </p>
        </div>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {notice && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(167, 139, 250, 0.12)',
              border: '1px solid rgba(167, 139, 250, 0.35)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}
          >
            {notice}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : scholars.length === 0 ? (
          <div className="card">
            <p style={{ color: 'var(--text-secondary)' }}>
              No scholars yet — scholars are created automatically when an application is approved.
            </p>
          </div>
        ) : (
          <>
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
                  <label htmlFor="scholarSearch">Search</label>
                  <input
                    id="scholarSearch"
                    placeholder="Student name or email"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
                  <label htmlFor="scholarBarangaySearch">Search by Barangay</label>
                  <input
                    id="scholarBarangaySearch"
                    value={barangaySearch}
                    onChange={(e) => setBarangaySearch(e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: '220px', flex: 1 }}>
                  <label htmlFor="programFilter">Filter by Program</label>
                  <select id="programFilter" value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}>
                    <option value="">All programs</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: '180px', flex: 1 }}>
                  <label htmlFor="scholarStatusFilter">Status</label>
                  <select id="scholarStatusFilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All statuses</option>
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0, minWidth: '200px', flex: 1 }}>
                  <label htmlFor="scholarCategoryFilter">Filter by Group</label>
                  <select id="scholarCategoryFilter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    {YEAR_LEVEL_CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {displayGroups.length === 0 ? (
              <div className="card">
                <p style={{ color: 'var(--text-secondary)' }}>No scholars match this filter.</p>
              </div>
            ) : (
              displayGroups.map((group) => (
                <section key={group.scholarshipName} style={{ marginBottom: '2rem' }}>
                  <h3 style={{ marginBottom: '0.75rem' }}>
                    {group.scholarshipName}{' '}
                    <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
                      ({group.scholars.length} scholar{group.scholars.length === 1 ? '' : 's'})
                    </span>
                  </h3>
                  <div className="card" style={{ overflowX: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Scholar ID</th>
                          <th>Student</th>
                          <th>Status</th>
                          <th>Date Submitted</th>
                          <th>Date Received</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.scholars.map((scholar, index) => (
                          <tr key={scholar.id}>
                            <td>{index + 1}</td>
                            <td>{scholar.scholarIdNumber ?? '—'}</td>
                            <td>
                              <div>{scholar.studentName}</div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{scholar.studentEmail}</div>
                            </td>
                            <td>
                              <span className={`badge ${STATUS_BADGE[scholar.status] ?? 'badge-secondary'}`}>
                                {scholar.status}
                              </span>
                            </td>
                            <td>{formatDate(scholar.submissionDate)}</td>
                            <td>{formatDate(scholar.receivedDate)}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Link className="btn btn-outline btn-sm" to={`/scholars/${scholar.id}`}>
                                  Manage
                                </Link>
                                {!isViewer && (
                                  <button
                                    className="btn btn-outline btn-sm"
                                    style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                                    onClick={() => setDeletingScholar(scholar)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </div>

      {deletingScholar && (
        <Modal title="Delete Scholar" onClose={() => setDeletingScholar(null)}>
          <p className="alert-error" style={{ padding: '0.85rem' }}>
            Delete <strong>{deletingScholar.studentName}</strong> from <strong>{deletingScholar.scholarshipName}</strong>?
            This permanently removes the scholar record, their approved application, and all associated grades,
            renewals, allowances, and violations. This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              style={{ background: 'var(--error-text)' }}
              disabled={deleting}
              onClick={handleConfirmDelete}
            >
              {deleting ? 'Deleting...' : 'Delete Scholar'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setDeletingScholar(null)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default ScholarManagementPage;
